import * as React from "react";
import {useMemo, useRef, useState} from "react";
import type {FieldError} from "@/shared/timr";
import {DurationUnit} from "@/shared/timrTypes";
import {DateRangePanel} from "@/shared/ui/dateRange/DateRangePanel";
import {DateTimeRangePanel} from "@/shared/ui/dateTimeRange/DateTimeRangePanel";
import type {DateRangeValue} from "@/shared/ui/dateRange/dateRangeState";
import type {DateTimeRangeValue} from "@/shared/ui/dateTimeRange/dateTimeRangeState";
import {
	foldDateRange,
	foldDateTime,
	toDateRangeValue,
	toDateTimeRangeValue,
	type WorkingTimeRangeValue
} from "@/shared/ui/workingTimeRange/workingTimeRangeState";
import {Temporal} from "@/shared/utils/datefunctions";

interface WorkingTimeRangePanelProps {
	/** The current range being edited; controlled — the parent holds it and passes back each {@link WorkingTimeRangePanelProps.onValueChange}. */
	value: WorkingTimeRangeValue;
	/** Selects the sub-panel: {@link DurationUnit.Minutes} renders the date-time panel, everything else the date panel (half-days enable the half-day checkboxes). */
	editUnit: DurationUnit;
	/** Server-side validation errors to surface on the matching fields of the active sub-panel. */
	fieldErrors: FieldError[];
	/** Renders the fields read-only; orthogonal to the edited data. */
	disabled: boolean;
	/** Called after every user edit with the next committed unified value. */
	onValueChange: (value: WorkingTimeRangeValue) => void;
	/** Label for the start-date field (day mode). */
	startLabel: string;
	/** Label for the end-date field (day mode). */
	endLabel: string;
	/** Whether the break-time field is editable (minute mode). */
	breakTimeEditable: boolean;
	/** The most recent record's end for the start field's "shift to last end" shortcut (minute mode); omit to hide it. */
	lastRecordEnd?: Temporal.ZonedDateTime | null;
	/** Reveal the timezone fields when the seeded offsets differ from the current zone; set when duplicating a record. */
	forceTimeZoneFields?: boolean;
	/** Re-anchor a calculated endpoint's offset to its local date instead of the persisted source offset; set when duplicating a record. */
	localizeCalculatedOffset?: boolean;
}

/**
 * A single façade over {@link DateRangePanel} (day / half-day) and {@link DateTimeRangePanel} (minute),
 * exposing one {@link WorkingTimeRangeValue} + one `onValueChange`. Only the sub-panel for the current
 * `editUnit` is mounted; the other mode's data rides along in the value and is projected in when it becomes
 * active (the edit-unit only ever changes together with a re-seed, so the newly-mounted panel always seeds
 * from the latest data).
 *
 * The seed handed to the active sub-panel keeps a stable object identity between re-seeds — the sub-panels
 * treat a new `value` object as the signal to discard in-progress edit state (e.g. the minute panel's
 * duration anchor), so only a genuine re-seed (dialog open, edit-unit switch) may change it. User edits are
 * tracked in a ref and emitted without re-seeding, and the value objects this panel emits itself are
 * recognised so echoing them straight back is not mistaken for a re-seed.
 */
export function WorkingTimeRangePanel({
	value: seededValue,
	editUnit,
	fieldErrors,
	disabled,
	onValueChange,
	startLabel,
	endLabel,
	breakTimeEditable,
	lastRecordEnd,
	forceTimeZoneFields = false,
	localizeCalculatedOffset = false
}: Readonly<WorkingTimeRangePanelProps>) {
	// `seed` changes identity only on a genuine re-seed; the memoised projections below inherit that identity
	// so the active sub-panel is never reseeded by the parent echoing an edit back.
	const [seed, setSeed] = useState(seededValue);
	const [lastSeeded, setLastSeeded] = useState(seededValue);
	// The live value: updated on every edit without a re-render, so the active sub-panel keeps its seed.
	const current = useRef(seededValue);
	const lastEmitted = useRef<WorkingTimeRangeValue | null>(null);
	if (lastSeeded !== seededValue) {
		setLastSeeded(seededValue);
		// A value this panel emitted itself (echoed straight back by the parent) is not a re-seed.
		if (seededValue !== lastEmitted.current) {
			current.current = seededValue;
			setSeed(seededValue);
		}
	}

	const dateRangeSeed = useMemo<DateRangeValue>(() => toDateRangeValue(seed), [seed]);
	const dateTimeRangeSeed = useMemo<DateTimeRangeValue>(() => toDateTimeRangeValue(seed), [seed]);

	const emit = (next: WorkingTimeRangeValue) => {
		current.current = next;
		lastEmitted.current = next;
		onValueChange(next);
	};

	if (editUnit === DurationUnit.Minutes) {
		return (
			<DateTimeRangePanel
				value={dateTimeRangeSeed}
				fieldErrors={fieldErrors}
				disabled={disabled}
				onValueChange={value => emit(foldDateTime(current.current, value))}
				lastRecordEnd={lastRecordEnd}
				breakTimeEditable={breakTimeEditable}
				forceTimeZoneFields={forceTimeZoneFields}
				localizeCalculatedOffset={localizeCalculatedOffset}
			/>
		);
	}
	return (
		<DateRangePanel
			value={dateRangeSeed}
			fieldErrors={fieldErrors}
			disabled={disabled}
			onValueChange={value => emit(foldDateRange(current.current, value))}
			startLabel={startLabel}
			endLabel={endLabel}
			halfDayMode={editUnit === DurationUnit.HalfDays}
		/>
	);
}

export type {WorkingTimeRangePanelProps};
